import { create } from 'zustand';
import { Role } from '../types/user';
import type { User, AreaManagerPermission, FarmerPermission } from '../types/user';
import { authService } from '../services/authService';
import { userService } from '../services/userService';
import { extractErrorMessage } from '../utils/errorUtils';
import { setAnalyticsUser, clearAnalyticsUser } from '../utils/analyticsUser';
import { useFieldStore } from './fieldStore';
import { useMemoStore } from './memoStore';

/**
 * 認証ストアの状態
 */
interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

/**
 * 認証ストアのアクション
 */
interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User | null) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  canWriteArea: (areaId: number) => boolean;
  canWriteFarm: (farmId: number) => boolean;
  getAccessibleAreaIds: () => number[];
  getAccessibleFarmIds: () => number[];
}

/**
 * 認証状態を管理するZustandストア
 *
 * ログイン・ログアウト・認証状態チェック・権限ヘルパーを提供する。
 * ログイン後に /me API を呼び出してロール・権限情報を取得する。
 */
export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  // 初期状態
  user: null,
  loading: false,
  error: null,
  initialized: false,

  /**
   * ログイン
   * Firebase 認証後、/me API でユーザー情報を取得する
   */
  login: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      await authService.login(email, password);
      const user = await userService.getMe();
      set({ user, loading: false, error: null });
      void setAnalyticsUser(user.uid, user.role);
    } catch (error: unknown) {
      set({
        loading: false,
        error: extractErrorMessage(error, 'ログインに失敗しました'),
        user: null,
      });
      throw error;
    }
  },

  /**
   * ログアウト
   */
  logout: async () => {
    set({ loading: true, error: null });
    try {
      await authService.logout();
      useFieldStore.getState().reset();
      // 前ユーザーのメモキャッシュが再ログイン後に漏れて表示されないようクリアする
      useMemoStore.getState().clearCache();
      set({ user: null, loading: false, error: null });
      void clearAnalyticsUser();
    } catch (error: unknown) {
      set({
        loading: false,
        error: extractErrorMessage(error, 'ログアウトに失敗しました'),
      });
      throw error;
    }
  },

  /**
   * 認証状態を確認
   * Firebase の認証状態変更を監視し、認証済みなら /me API を呼ぶ
   */
  checkAuth: async () => {
    set({ loading: true });

    return new Promise<void>((resolve) => {
      const unsubscribe = authService.onAuthStateChanged(async (isAuthenticated) => {
        if (isAuthenticated) {
          try {
            const user = await userService.getMe();
            set({ user, loading: false, initialized: true });
            void setAnalyticsUser(user.uid, user.role);
          } catch {
            set({ user: null, loading: false, initialized: true });
            void clearAnalyticsUser();
          }
        } else {
          set({ user: null, loading: false, initialized: true });
          void clearAnalyticsUser();
        }
        unsubscribe();
        resolve();
      });
    });
  },

  /**
   * ユーザーを設定
   */
  setUser: (user: User | null) => {
    set({ user });
  },

  /**
   * エラーを設定
   */
  setError: (error: string | null) => {
    set({ error });
  },

  /**
   * エラーをクリア
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * 指定した地区に書き込み権限があるか判定する
   * @param areaId - 地区 ID
   * @returns rw 権限がある場合 true
   */
  canWriteArea: (areaId: number): boolean => {
    const { user } = get();
    if (!user) return false;
    if (user.role === Role.ADMIN) return true;
    if (user.role === Role.AREA_MANAGER) {
      const perm = user.permission as AreaManagerPermission;
      return perm.areas?.some((a) => a.id === areaId && a.permission === 'rw') ?? false;
    }
    return false;
  },

  /**
   * 指定した農場に書き込み権限があるか判定する
   * @param farmId - 農場 ID
   * @returns rw 権限がある場合 true
   */
  canWriteFarm: (farmId: number): boolean => {
    const { user } = get();
    if (!user) return false;
    if (user.role === Role.ADMIN) return true;
    if (user.role === Role.FARMER) {
      const perm = user.permission as FarmerPermission;
      return perm.farms?.some((f) => f.id === farmId && f.permission === 'rw') ?? false;
    }
    return false;
  },

  /**
   * アクセス可能な地区 ID 一覧を取得する
   * @returns 地区 ID の配列
   */
  getAccessibleAreaIds: (): number[] => {
    const { user } = get();
    if (!user) return [];
    if (user.role === Role.ADMIN) return []; // ADMIN は全地区アクセス可（空 = 制限なし）
    if (user.role === Role.AREA_MANAGER) {
      const perm = user.permission as AreaManagerPermission;
      return perm.areas?.map((a) => a.id) ?? [];
    }
    return [];
  },

  /**
   * アクセス可能な農場 ID 一覧を取得する
   * @returns 農場 ID の配列
   */
  getAccessibleFarmIds: (): number[] => {
    const { user } = get();
    if (!user) return [];
    if (user.role === Role.ADMIN) return []; // ADMIN は全農場アクセス可（空 = 制限なし）
    if (user.role === Role.FARMER) {
      const perm = user.permission as FarmerPermission;
      return perm.farms?.map((f) => f.id) ?? [];
    }
    return [];
  },
}));
